import { useCallback, useEffect } from 'react';
import { getCurrentUserId } from '../../../../infra/firebase/init';
import useSocket from '../../../../shared/hooks/use-socket';
import { useReducerAtom } from 'jotai/utils';
import { socketConnectionAtom, socketConnectionReducer } from '../../../../shared/states/socket-connection';
import { SocketConnectionReducerActionTypes, SocketName } from '../../../../shared/typings/socket-connection';
import { getUserChannelData } from '../../../login/v1/utility';
import { getOrganizationsSocket } from '../../../../infra/sockets/organization-socket';
import { WACallPayload, WACallsEventTypes } from '../../../call-manager/typings';

export const useOrganizationCallsSocket = () => {
  const currentUserId = getCurrentUserId();
  const orgName = getUserChannelData()?.name ?? 'Doubletick';
  const [_, dispatch] = useReducerAtom(socketConnectionAtom, socketConnectionReducer);

  const { socket: organisationSocket } = useSocket(getOrganizationsSocket, undefined, true, (status) =>
    dispatch({
      type: SocketConnectionReducerActionTypes.CONNECTION,
      data: {
        name: SocketName.ORGANIZATION,
        status,
      },
    })
  );

  const onCallConnectRequest = useCallback((payload: WACallPayload) => {
    switch (payload.event) {
      case WACallsEventTypes.INCOMMING_CALL:
        const {
          customerName,
          customerNumber,
          wabaNumber,
          wabaName,
          callId,
          sdp,
          sdpType,
          startTimestamp,
          expiryTimestamp,
        } = payload;
        // check if expiry_time is in the past
        if (payload.expiryTimestamp && new Date(payload.expiryTimestamp) < new Date()) {
          return;
        }
        window.postMessage(
          {
            type: 'INCOMING_CALL',
            payload: {
              customerName,
              customerNumber,
              wabaNumber,
              wabaName,
              callId,
              sdp,
              sdpType,
              dateCreated: new Date(Number(startTimestamp)).toISOString(),
              expiryTimestamp: expiryTimestamp ? Number(expiryTimestamp) : undefined,
            },
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_TERMINATE:
        const { duration, direction, startTime, endTime, callId: terminatedCallId, endedBy } = payload;
        window.postMessage(
          {
            type: 'CALL_TERMINATE',
            payload: {
              duration,
              direction,
              startTime,
              endTime,
              endedBy,
              callId: terminatedCallId,
            },
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_PICKED:
        const { userId, callId: pickedCallId } = payload;
        window.postMessage(
          {
            type: 'CALL_PICKED',
            payload: {
              userId,
              callId: pickedCallId,
              direction: payload.direction,
              sdp: payload.sdp,
              sdpType: payload.sdpType,
            },
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_STATUS_UPDATES:
        const { callId: statusCallId, status } = payload;
        window.postMessage(
          {
            type: 'CALL_STATUS_UPDATES',
            payload: { callId: statusCallId, status },
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_PERMISSION:
        window.postMessage(
          {
            type: 'CALL_PERMISSION',
            payload,
          },
          '*'
        );
        break;
      case WACallsEventTypes.CALL_RECONNECTED:
        window.postMessage(
          {
            type: 'CALL_RECONNECTED',
            payload,
          },
          '*'
        );
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    /**
     * This event handler is for incoming call requests
     */
    organisationSocket.on('wa-calls', onCallConnectRequest);

    return () => {
      organisationSocket.off('wa-calls', onCallConnectRequest);
    };
  }, [onCallConnectRequest]);
};
